import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import { StepLayoutContainer } from "components/Layout/StepLayoutContainer";
import { FocusFeedbackChip } from "../Components";
import { ACTIONS, STEPS } from "../config";
import { FC, useState, useMemo } from "react";
import { ErrorType, RequestFeedbackData } from "../types";
import { SummaryBox } from "../Components";
import { RequestFeedbackSteps } from "helpers/constants";
import { RateSelector } from "../Components";
import { Tag } from "types/tag";
import { Edit } from "@mui/icons-material";
import { CustomLabel } from "types/CustomLabel";
import { REQUIREMENT } from "helpers/constants";
import dayjs from "dayjs";
import { SubRequirementsModal } from "components/Feedback/SubRequirementsModal";
import { FeedbackType } from "types/feedback";
import { Requirement } from "types/requirement";
import { getRequirementDisplayData } from "utils/feedbackIter";

type SubmitProps = {
  data: RequestFeedbackData;
  onUpdate: (data: any) => void;
  handleStep: (step: string, action?: string) => void;
  onSubmit: () => void;
  error: ErrorType;
  tags: Tag[];
  displayTimer: boolean;
  customLabels: CustomLabel[];
  historicalFeedbacks?: FeedbackType[];
  allRequirements?: Requirement[];
  hideLearnerReflect?: boolean;
};

export const Submit: FC<SubmitProps> = ({
  data,
  onUpdate,
  onSubmit,
  handleStep,
  error,
  tags,
  displayTimer,
  customLabels,
  historicalFeedbacks = [],
  allRequirements = [],
  hideLearnerReflect = false,
}) => {
  const [openSubRequirementsModal, setOpenSubRequirementsModal] =
    useState(false);

  const isITER = useMemo(
    () => data?.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER,
    [data?.selectedCompetencySkill?.type],
  );

  const subRequirements = useMemo(() => {
    if (isITER && data.selectedCompetencySkill?.competencies) {
      return data.selectedCompetencySkill.competencies
        .map((c: any) => c.competency)
        .filter(Boolean);
    }
    return [];
  }, [isITER, data.selectedCompetencySkill]);

  // Build ratings by requirement for ITER competencies
  const buildRatingsByRequirementForIter = useMemo(() => {
    if (!isITER || !data.selectedCompetencySkill?.name) {
      return {};
    }

    const ratingsByRequirement: Record<
      string,
      {
        date: string;
        learnerRating?: number | null;
        coachRating?: number | null;
      }[]
    > = {};

    const iterName = data.selectedCompetencySkill.name;
    const history = historicalFeedbacks
      .filter((fb) => {
        const requirementData = getRequirementDisplayData(fb, allRequirements);
        return requirementData.isITER && requirementData.name === iterName;
      })
      .sort((a, b) => {
        const da = a.observation_date || a.created_at || "";
        const db = b.observation_date || b.created_at || "";
        return da.localeCompare(db);
      });

    history.forEach((fb) => {
      const date = (fb.observation_date || fb.created_at) as string;
      fb.feedback_skills.forEach((skill) => {
        const reqId = skill.requirement?.requirement_id;
        if (!reqId) return;
        if (!ratingsByRequirement[reqId]) {
          ratingsByRequirement[reqId] = [];
        }
        ratingsByRequirement[reqId].push({
          date,
          learnerRating: skill.self_rating ?? null,
          coachRating: skill.coach_rating ?? null,
        });
      });
    });

    return ratingsByRequirement;
  }, [
    isITER,
    data.selectedCompetencySkill,
    historicalFeedbacks,
    allRequirements,
  ]);

  // Format observation date for display
  const observationDate = useMemo(() => {
    if (data.observationDate) {
      return dayjs(data.observationDate).format("MMMM DD, YYYY");
    }
    if (data.caseDate) {
      return dayjs(data.caseDate).format("MMMM DD, YYYY");
    }
    return null;
  }, [data.observationDate, data.caseDate]);

  return (
    <StepLayoutContainer
      title={
        hideLearnerReflect
          ? RequestFeedbackSteps.submitTitleGiveFeedback
          : "Submit Request for Completion and Sign-Off"
      }
      description={
        hideLearnerReflect
          ? "Review your feedback and submit it for the learner."
          : "This will be sent to your coach to evaluate your performance at a later time."
      }
      renderActionButtons={
        <Button variant="contained" color="error" fullWidth onClick={onSubmit}>
          Submit
        </Button>
      }
      onClickBack={() =>
        handleStep(
          data.coachAvailable || hideLearnerReflect
            ? STEPS.REFLECT_COACH
            : STEPS.REFLECT,
          "back",
        )
      }
      marginXSBottom={10}
      reduceHeightTOP={displayTimer ? 4.25 : 0}
    >
      <Box mt={2}>
        {!data.coachAvailable && (
          <Box mt={4}>
            <Typography variant="body1" fontWeight="bold">
              What would you like your coach to focus their feedback on?{" "}
              <Typography
                variant="body1"
                fontWeight="normal"
                color="text.secondary"
              >
                (Select all that apply)
              </Typography>
            </Typography>

            <Box mt={2}>
              <Box>
                {tags.map((tag) => (
                  <FocusFeedbackChip
                    key={tag.tag_id}
                    focus={tag}
                    selectedFocusFeedback={data.selectedFocusFeedback}
                    onUpdate={(payload) =>
                      onUpdate({
                        type: ACTIONS.SET_SELECTED_FOCUS_FEEDBACK,
                        payload,
                      })
                    }
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}
        {!isITER && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box mt={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h6">Summary</Typography>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    handleStep(STEPS.REQUEST, "back");
                  }}
                >
                  <Edit />
                </IconButton>
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Box mt={2}>
                <SummaryBox data={data} />
              </Box>
            </Box>
          </>
        )}
        {isITER && observationDate && (
          <Box mt={2}>
            <Typography variant="body1">
              <strong>Date:</strong> {observationDate}
            </Typography>
          </Box>
        )}
      </Box>

      {!hideLearnerReflect && (
        <>
          <Box mt={4}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6">Personal Reflection</Typography>
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  handleStep(STEPS.REFLECT, "back");
                }}
              >
                <Edit />
              </IconButton>
            </Stack>
            <Divider sx={{ my: 1 }} />
          </Box>

          {isITER && subRequirements.length > 0 ? (
            // For ITER: Group both questions per competency
            <Box my={2}>
              {subRequirements.map((subReq: any, index: number) => (
                <Box
                  key={subReq.requirement_id}
                  sx={{
                    mb: 3,
                    borderTop: index > 0 ? "1px solid rgba(0,0,0,0.2)" : "none",
                    pt: index > 0 ? 3 : 0,
                    mt: index > 0 ? 3 : 0,
                  }}
                >
                  {/* Competency Name */}
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Typography variant="h6" fontWeight={600}>
                      {subReq.name}
                    </Typography>
                    <Tooltip title="View Competencies">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenSubRequirementsModal(true);
                        }}
                      >
                        <Info fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {subReq.description && (
                    <Typography
                      variant="body2"
                      sx={{ mb: 2, color: "text.secondary" }}
                    >
                      {subReq.description}
                    </Typography>
                  )}

                  {/* Question 1: Comment */}
                  <Box mb={3}>
                    <Typography variant="body1" fontWeight="bold">
                      1. {RequestFeedbackSteps.question1Reflect}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="normal"
                      color="text.secondary"
                    >
                      {data.iterSubRequirementComments[subReq.requirement_id] ||
                        ""}
                    </Typography>
                  </Box>

                  {/* Question 2: Rating */}
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      2. {RequestFeedbackSteps.question2Reflect}
                    </Typography>
                    <Box mt={1}>
                      <RateSelector
                        value={
                          data.iterSubRequirementRates?.[
                            subReq.requirement_id
                          ] || null
                        }
                        requirement={subReq}
                        customLabels={customLabels}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            // For non-ITER: Show questions separately
            <>
              <Box my={2}>
                <Typography variant="body1" fontWeight="bold">
                  {RequestFeedbackSteps.question1Reflect}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="normal"
                  color="text.secondary"
                >
                  {data.perform}
                </Typography>
              </Box>

              <Box my={2}>
                <Typography variant="body1" fontWeight="bold">
                  {RequestFeedbackSteps.question2Reflect}
                </Typography>
                <RateSelector
                  value={data.rate}
                  requirement={data.selectedCompetencySkill}
                  customLabels={customLabels}
                />
              </Box>
            </>
          )}
        </>
      )}

      {/* Coach Feedback */}
      {(data.coachAvailable || hideLearnerReflect) && (
        <Box>
          <Box mt={4}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6">
                {hideLearnerReflect
                  ? RequestFeedbackSteps.sectionGiveFeedback
                  : "Coach Feedback"}
              </Typography>
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  handleStep(STEPS.REFLECT_COACH, "back");
                }}
              >
                <Edit />
              </IconButton>
            </Stack>
            <Divider sx={{ my: 1 }} />
          </Box>

          <Box my={2}>
            <Typography variant="body1" fontWeight="bold">
              {hideLearnerReflect
                ? RequestFeedbackSteps.questionFocusFeedbackGive
                : RequestFeedbackSteps.questionFocusFeedback}
            </Typography>

            <Stack direction="row" spacing={1} mt={1}>
              {data.selectedFocusFeedback.map((tag) => (
                <Chip
                  key={tag.tag_id}
                  label={tag.name}
                  color="primary"
                  variant="filled"
                  onClick={() => {}}
                />
              ))}
            </Stack>
          </Box>

          <Box my={2}>
            <Typography variant="body1" fontWeight="bold">
              {hideLearnerReflect
                ? RequestFeedbackSteps.question1ReflectCoachDetail
                : RequestFeedbackSteps.question1ReflectCoach}
            </Typography>
            <Typography
              variant="body1"
              fontWeight="normal"
              color="text.secondary"
            >
              {data.coachObserve}
            </Typography>
          </Box>

          <Box my={2}>
            <Typography variant="body1" fontWeight="bold">
              {hideLearnerReflect
                ? RequestFeedbackSteps.question2ReflectCoachDetail
                : RequestFeedbackSteps.question2ReflectCoach}
            </Typography>
            {isITER && subRequirements.length > 0 ? (
              <Box mt={2}>
                {subRequirements.map((subReq: any, index: number) => (
                  <Box key={subReq.requirement_id} sx={{ mb: 3 }}>
                    <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                      {subReq.name}
                    </Typography>
                    {subReq.description && (
                      <Typography
                        variant="body2"
                        sx={{ mb: 2, color: "text.secondary" }}
                      >
                        {subReq.description}
                      </Typography>
                    )}
                    <RateSelector
                      value={
                        data.iterSubRequirementCoachRates?.[
                          subReq.requirement_id
                        ] || null
                      }
                      requirement={subReq}
                      customLabels={customLabels}
                    />
                    {index < subRequirements.length - 1 && (
                      <Divider sx={{ mt: 3 }} />
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <RateSelector
                value={data.coachRate}
                requirement={data.selectedCompetencySkill}
                customLabels={customLabels}
              />
            )}
          </Box>
        </Box>
      )}

      <Box my={4}>
        {/* {isInTime && (
          <Alert severity="info">
            When you click on <strong>Submit</strong>, this Feedback Request
            form will be sent to your selected Coach for official sign-off. Any
            edits to the Feedback Request form will not be permitted until after
            the Coach reviews.
          </Alert>
        )} */}
      </Box>

      {isITER && data.selectedCompetencySkill && (
        <SubRequirementsModal
          open={openSubRequirementsModal}
          onClose={() => setOpenSubRequirementsModal(false)}
          iterName={data.selectedCompetencySkill.name}
          subRequirements={subRequirements.map((subReq: any) => ({
            requirement_id: subReq.requirement_id,
            name: subReq.name,
            description: subReq.description || null,
          }))}
          ratingsByRequirement={buildRatingsByRequirementForIter}
        />
      )}
    </StepLayoutContainer>
  );
};
